import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../services/api';

export interface RawIotDevice {
  [key: string]: any;
}

export interface RawZone {
  id: string;
  entry_type?: string;
  [key: string]: any;
}

interface DevicesAndZones {
  devices: RawIotDevice[];
  zones: RawZone[];
}

const EMPTY: DevicesAndZones = { devices: [], zones: [] };

// De-dupes concurrent requests for the same customer. Multiple independent parts of the
// app (Layout's Botoeira widget, OperationalView's device list) each used to fire their
// own /iot-devices + /zones fetch pair whenever a customer became active, doubling the
// same network calls every time. This collapses simultaneous callers into one fetch.
const inflight = new Map<string, Promise<DevicesAndZones>>();

// Test-only escape hatch: this module's state otherwise persists for the lifetime of the
// Jest module registry, so a promise still pending when one test ends (e.g. a test that
// doesn't await the fetch before finishing) leaks into the next test that reuses the same
// customerId, silently starving it of its own mocked fetchWithAuth call. Call this from
// beforeEach/afterEach in tests that mock fetchWithAuth and re-render with the same customerId.
export function __resetCustomerDevicesAndZonesCacheForTests() {
  inflight.clear();
}

async function fetchDevicesAndZones(customerId: string): Promise<DevicesAndZones> {
  const existing = inflight.get(customerId);
  if (existing) return existing;

  const promise = (async (): Promise<DevicesAndZones> => {
    const [devicesRes, zonesRes] = await Promise.all([
      fetchWithAuth(`/api/v1/iot-devices/customer/${customerId}`),
      fetchWithAuth(`/api/v1/zones/customer/${customerId}`),
    ]);
    const devices = devicesRes.ok ? (await devicesRes.json()).devices || [] : [];
    const zones = zonesRes.ok ? (await zonesRes.json()).zones || [] : [];
    return { devices, zones };
  })();

  inflight.set(customerId, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(customerId);
  }
}

// Fetches the raw device + zone lists for a customer, shared/de-duped across every
// consumer. Each consumer maps the raw shape to whatever local shape it needs.
export function useCustomerDevicesAndZones(customerId: string | null | undefined) {
  const [data, setData] = useState<DevicesAndZones>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!customerId) {
      setData(EMPTY);
      return;
    }
    setIsLoading(true);
    try {
      const result = await fetchDevicesAndZones(customerId);
      if (mountedRef.current) setData(result);
    } catch {
      if (mountedRef.current) setData(EMPTY);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { devices: data.devices, zones: data.zones, isLoading, refetch };
}
